import type { MessageActionRequest, SpecialUse, ThreadRow } from '@email/shared';
import {
  Archive,
  Clock3,
  ExternalLink,
  Inbox,
  Mail,
  MailOpen,
  MessageSquare,
  ShieldAlert,
  ShieldCheck,
  Star,
  Trash2,
} from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface MessageContextMenuProps {
  x: number;
  y: number;
  row: ThreadRow;
  targetRows: ThreadRow[];
  currentSpecialUse: SpecialUse | null;
  pending: boolean;
  onClose: () => void;
  onOpen: () => void;
  onOpenNewTab: () => void;
  onAction: (action: MessageActionRequest['action']) => void;
  onMoveToInbox?: (() => void) | undefined;
  onSnooze: () => void;
  onUnsnooze: () => void;
  snoozedView?: boolean;
}

const VIEWPORT_MARGIN = 8;

export function MessageContextMenu({
  x,
  y,
  row,
  targetRows,
  currentSpecialUse,
  pending,
  onClose,
  onOpen,
  onOpenNewTab,
  onAction,
  onMoveToInbox,
  onSnooze,
  onUnsnooze,
  snoozedView = false,
}: MessageContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef(document.activeElement as HTMLElement | null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [position, setPosition] = useState({ left: x, top: y });
  const allRead = targetRows.every((target) => !target.unread);
  const allStarred = targetRows.every((target) => target.starred);
  const canReportSpam =
    currentSpecialUse !== 'trash' &&
    currentSpecialUse !== 'sent' &&
    currentSpecialUse !== 'drafts';

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(x, window.innerWidth - menu.offsetWidth - VIEWPORT_MARGIN),
    );
    const top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(y, window.innerHeight - menu.offsetHeight - VIEWPORT_MARGIN),
    );
    setPosition({ left, top });
    firstItemRef.current?.focus();
  }, [x, y]);

  useEffect(() => {
    const closeIfOutside = (event: Event) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) {
        onCloseRef.current();
      }
    };
    const closeOnViewportChange = () => onCloseRef.current();
    const closeOnOutsideScroll = (event: Event) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      onCloseRef.current();
    };
    document.addEventListener('pointerdown', closeIfOutside, true);
    document.addEventListener('contextmenu', closeIfOutside, true);
    window.addEventListener('resize', closeOnViewportChange);
    window.addEventListener('scroll', closeOnOutsideScroll, true);
    return () => {
      document.removeEventListener('pointerdown', closeIfOutside, true);
      document.removeEventListener('contextmenu', closeIfOutside, true);
      window.removeEventListener('resize', closeOnViewportChange);
      window.removeEventListener('scroll', closeOnOutsideScroll, true);
      previousFocusRef.current?.focus();
    };
  }, []);

  const invoke = (command: () => void) => {
    onClose();
    command();
  };

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="Conversation actions"
      onContextMenu={(event) => event.preventDefault()}
      onKeyDown={(event) => {
        const items = [...(menuRef.current?.querySelectorAll<HTMLButtonElement>(
          '[role="menuitem"]:not(:disabled)',
        ) ?? [])];
        const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
        let nextIndex: number | null = null;
        if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % items.length;
        if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + items.length) % items.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = items.length - 1;
        const nextItem = nextIndex === null ? undefined : items[nextIndex];
        if (nextItem) {
          event.preventDefault();
          nextItem.focus();
        } else if (event.key === 'Escape' || event.key === 'Tab') {
          if (event.key === 'Escape') event.preventDefault();
          onClose();
        }
      }}
      className="fixed z-[90] max-h-[calc(100vh-1rem)] w-72 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-xl border border-line bg-surface p-1.5 shadow-2xl"
      style={position}
    >
      <div className="border-b border-line px-3 py-2">
        <p className="truncate text-sm font-semibold">{row.subject || '(no subject)'}</p>
        <p className="mt-0.5 text-xs text-ink-muted">
          {targetRows.length === 1
            ? '1 conversation'
            : `${targetRows.length.toLocaleString()} selected conversations`}
        </p>
      </div>
      <div className="py-1">
        <MenuItem
          ref={firstItemRef}
          icon={<MessageSquare className="size-4" />}
          label="Open"
          onClick={() => invoke(onOpen)}
        />
        <MenuItem
          icon={<ExternalLink className="size-4" />}
          label="Open in new tab"
          onClick={() => invoke(onOpenNewTab)}
        />
      </div>
      <div role="separator" className="border-t border-line" />
      <div className="py-1">
        <MenuItem
          icon={allRead ? <Mail className="size-4" /> : <MailOpen className="size-4" />}
          label={allRead ? 'Mark unread' : 'Mark read'}
          disabled={pending}
          onClick={() => invoke(() => onAction(allRead ? 'unread' : 'read'))}
        />
        <MenuItem
          icon={
            <Star
              className={`size-4 ${allStarred ? 'fill-amber-400 text-amber-400' : ''}`}
            />
          }
          label={allStarred ? 'Remove star' : 'Star'}
          disabled={pending}
          onClick={() => invoke(() => onAction(allStarred ? 'unstar' : 'star'))}
        />
        {currentSpecialUse === 'inbox' || snoozedView ? (
          <MenuItem
            icon={<Clock3 className="size-4" />}
            label={snoozedView ? 'Return to inbox now' : 'Snooze'}
            disabled={pending}
            onClick={() => invoke(snoozedView ? onUnsnooze : onSnooze)}
          />
        ) : null}
        {currentSpecialUse !== 'archive' && currentSpecialUse !== 'all' ? (
          <MenuItem
            icon={<Archive className="size-4" />}
            label="Archive"
            disabled={pending}
            onClick={() => invoke(() => onAction('archive'))}
          />
        ) : null}
        {currentSpecialUse === 'junk' ? (
          <MenuItem
            icon={<ShieldCheck className="size-4" />}
            label="Not spam"
            disabled={pending}
            onClick={() => invoke(() => onAction('not_spam'))}
          />
        ) : canReportSpam ? (
          <MenuItem
            icon={<ShieldAlert className="size-4" />}
            label="Report spam"
            disabled={pending}
            onClick={() => invoke(() => onAction('spam'))}
          />
        ) : null}
        {currentSpecialUse === 'trash' ? (
          <>
            <MenuItem
              icon={<Inbox className="size-4" />}
              label="Move to Inbox"
              disabled={pending || !onMoveToInbox}
              onClick={() => invoke(() => onMoveToInbox?.())}
            />
            <MenuItem
              icon={<Trash2 className="size-4" />}
              label="Delete forever"
              disabled={pending}
              danger
              onClick={() => invoke(() => onAction('delete_perm'))}
            />
          </>
        ) : (
          <MenuItem
            icon={<Trash2 className="size-4" />}
            label="Move to Trash"
            disabled={pending}
            danger
            onClick={() => invoke(() => onAction('trash'))}
          />
        )}
      </div>
    </div>
  );
}

const MenuItem = forwardRef<
  HTMLButtonElement,
  {
    icon: ReactNode;
    label: string;
    disabled?: boolean;
    danger?: boolean;
    onClick: () => void;
  }
>(function MenuItem({ icon, label, disabled = false, danger = false, onClick }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm disabled:opacity-40 ${
        danger ? 'text-danger hover:bg-danger/10' : 'hover:bg-hover'
      }`}
    >
      <span className={`shrink-0 ${danger ? 'text-danger' : 'text-ink-muted'}`}>{icon}</span>
      <span>{label}</span>
    </button>
  );
});
